import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import taxService from '../../services/taxService';
import '../../styles/ImsTheme.css';
import './ProductCategoryEditPage.css';
import { ArrowLeft, Save } from 'lucide-react';

import { getServerErrorMessage } from '../../utils/errorUtils';
// Max length constants for validation
const MAX_LENGTHS = {
  taxName: 30,
  description: 200,
};

interface TaxForm {
  taxName: string;
  taxPercentage: number | '';
  applicableFor: string;
  description: string;
}

interface FieldErrors {
  taxName?: string;
  taxPercentage?: string;
  description?: string;
}

const mapFormToDTO = (form: TaxForm) => ({
  taxName: form.taxName,
  taxType: 'VAT',
  taxPercentage: form.taxPercentage || 0,
  taxCalculationMethod: 'Exclusive',
  applicableFor: form.applicableFor,
  isCompoundTax: false,
  description: form.description,
});

const TaxCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formData, setFormData] = useState<TaxForm>({
    taxName: '',
    taxPercentage: '',
    applicableFor: 'Both',
    description: '',
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Enforce max length by truncating (generic key-lookup)
    let processedValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      processedValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: name === 'taxPercentage' ? (processedValue ? parseFloat(processedValue) : '') : processedValue,
    }));

    // Clear field error when user starts typing
    setFieldErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // Validate Tax Name
    if (!formData.taxName.trim()) {
      errors.taxName = t('tax.formValidation.nameRequired');
      isValid = false;
    } else if (formData.taxName.length > MAX_LENGTHS.taxName) {
      errors.taxName = t('tax.formValidation.nameMaxLength');
      isValid = false;
    }

    // Validate Tax Percentage
    if (formData.taxPercentage === '' || formData.taxPercentage === null) {
      errors.taxPercentage = t('tax.formValidation.taxRateRequired');
      isValid = false;
    } else if (typeof formData.taxPercentage === 'number' && formData.taxPercentage < 0) {
      errors.taxPercentage = t('tax.formValidation.taxRateNegative');
      isValid = false;
    }

    // Validate Description (optional field — max length only)
    if (formData.description && formData.description.length > MAX_LENGTHS.description) {
      errors.description = t('tax.formValidation.descriptionMaxLength');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      // Display first error message in general error section
      const firstError = Object.values(errors).find((error) => error);
      if (firstError) {
        setError(firstError);
      }
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dto = mapFormToDTO(formData);
      await taxService.createTax(dto);
      navigate('/products/taxes', {
        state: { message: t('tax.createSuccess') },
      });
    } catch (err: any) {
      const errorMessage = getServerErrorMessage(err) || t('tax.failedToCreateTax');
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="edit-container">
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/taxes')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">{t('tax.newTax')}</div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/taxes')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={loading}>
              {loading ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.save')}</>}
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('tax.createNewTax')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit} noValidate>
                  <div className="form-group">
                    <label className="form-label">
                      {t('tax.taxName')} <span className="required">*</span>
                      <span className="char-counter">{formData.taxName.length}/{MAX_LENGTHS.taxName}</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input ${fieldErrors.taxName ? 'is-invalid' : ''}`}
                      name="taxName"
                      value={formData.taxName}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.taxName}
                      placeholder={t('tax.taxName')}
                    />
                    {fieldErrors.taxName && <small className="form-error">{fieldErrors.taxName}</small>}
                    {!fieldErrors.taxName && <small className="form-helper">{t('tax.taxNameHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('tax.taxRate')} <span className="required">*</span>
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        className={`form-input ${fieldErrors.taxPercentage ? 'is-invalid' : ''}`}
                        name="taxPercentage"
                        value={formData.taxPercentage}
                        onChange={handleInputChange}
                        placeholder="0"
                        step="0.01"
                        style={{ flex: 1 }}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '18px' }}>%</span>
                    </div>
                    {fieldErrors.taxPercentage && <small className="form-error">{fieldErrors.taxPercentage}</small>}
                    {!fieldErrors.taxPercentage && <small className="form-helper">{t('tax.taxRateHint')}</small>}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('tax.applicableFor')} <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      name="applicableFor"
                      value={formData.applicableFor}
                      onChange={handleInputChange}
                    >
                      <option value="Sales">{t('tax.applicableForSales')}</option>
                      <option value="Purchase">{t('tax.applicableForPurchase')}</option>
                      <option value="Both">{t('tax.applicableForBoth')}</option>
                    </select>
                    <small className="form-helper">{t('tax.applicableForHint')}</small>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('tax.description')}
                      <span className="char-counter">{formData.description.length}/{MAX_LENGTHS.description}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.description ? 'is-invalid' : ''}`}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      maxLength={MAX_LENGTHS.description}
                      placeholder={t('tax.description')}
                      rows={4}
                    />
                    {fieldErrors.description && <small className="form-error">{fieldErrors.description}</small>}
                    {!fieldErrors.description && <small className="form-helper">{t('tax.descriptionHint')}</small>}
                  </div>

                </form>
              </div>
            </div>
          </div>

          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="help-item">
                  <strong>Tax Code</strong>
                  <p>A unique identifier is automatically generated by the system when the tax is created. You cannot modify it.</p>
                </div>
                <div className="help-item">
                  <strong>Tax Name</strong>
                  <p>Enter a descriptive name for the tax (max 30 characters, e.g., VAT, GST, Service Tax).</p>
                </div>
                <div className="help-item">
                  <strong>Tax Rate</strong>
                  <p>Enter the percentage value for this tax (must be 0 or greater).</p>
                </div>
                <div className="help-item">
                  <strong>Applicable For</strong>
                  <p>Choose whether this tax applies to sales transactions, purchase transactions, or both.</p>
                </div>
                <div className="help-item">
                  <strong>Description</strong>
                  <p>Optional field to provide additional details about this tax (max 200 characters).</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TaxCreatePage;
